import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings, formatNumber } from '../context/SettingsContext';
import { createExpense, updateExpense, searchVehicles, scanReceipt, getAiQuota, createRecurringExpense } from '../api';
import type { Expense, ExpenseCategory, VatStatus, } from '../types';
import type { ScanQuota } from '../api';
import PriceInput from './PriceInput';
import ExpenseAttachmentsSection from './ExpenseAttachmentsSection';

interface Props {
  // Med id → redigér. Uden id → opret, forudfyldt med de felter der er sat.
  // Partial, fordi EventModal åbner formularen med kun en bil valgt.
  expense?: Partial<Expense>;
  onSaved: () => void;
  onClose: () => void;
}

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

export default function ExpenseModal({ expense, onSaved, onClose }: Props) {
  const { t } = useTranslation();
  const { settings } = useSettings();
  const [date, setDate] = useState(expense?.date?.slice(0, 10) ?? today());
  const [category, setCategory] = useState<ExpenseCategory>((expense?.category as ExpenseCategory) ?? 'other');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [amount, setAmount] = useState(expense?.amount != null ? formatNumber(expense.amount, settings.numberFormat) : '');
  const [vatStatus, setVatStatus] = useState<VatStatus>((expense?.vat_status as VatStatus) ?? 'full');
  const [vatAmount, setVatAmount] = useState(expense?.vat_amount != null ? formatNumber(expense.vat_amount, settings.numberFormat) : '');
  const [notes, setNotes] = useState(expense?.notes ?? '');
  const [vehicleId, setVehicleId] = useState<number | null>(expense?.vehicle_id ?? null);
  const [vehicleLabel, setVehicleLabel] = useState(expense?.registration_number ?? '');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleResults, setVehicleResults] = useState<{ id: number; registration_number: string }[]>([]);
  const [editing, setEditing] = useState(!expense?.id);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<'monthly' | 'yearly'>('monthly');
  const [saving, setSaving] = useState(false);

  // ── Scan state ──────────────────────────────────────────────────────────
  type ScanState = 'idle' | 'scanning' | 'success' | 'failed';
  const [scanState, setScanState]   = useState<ScanState>('idle');
  const [scanError, setScanError]   = useState<string | null>(null);
  const [quota, setQuota]           = useState<ScanQuota | null>(null);
  const fileInputRef                = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Load quota when opening create form so Pro Scan saldo is ready
    if (!expense?.id) getAiQuota().then(setQuota).catch(() => null);
  }, [expense?.id]);

  useEffect(() => {
    if (!vehicleSearch) { setVehicleResults([]); return; }
    const timer = setTimeout(async () => {
      setVehicleResults(await searchVehicles(vehicleSearch));
    }, 300);
    return () => clearTimeout(timer);
  }, [vehicleSearch]);

  // "Værksted" kan ikke vælges på en ny udgift: en værkstedsregning skal registreres som en
  // begivenhed, så den også står i bilens historik — det er den der fortæller hvornår tingene
  // sidst blev lavet. Begivenheden opretter selv den matchende udgift.
  // Findes kategorien allerede på rækken (de gamle, eller en udgift født af en begivenhed),
  // bliver den stående, så redigering ikke stiltiende flytter den til en anden kategori.
  // Betingelsen ser på den aktuelle værdi, ikke kun den oprindelige: bilagsscanningen kan selv
  // sætte kategorien til værksted, og så skal valget stadig kunne vises i dropdown'en.
  const ALL_CATEGORIES: ExpenseCategory[] = ['insurance', 'tax', 'cleaning', 'parking', 'workshop', 'other'];
  const selectableCategories = category === 'workshop'
    ? ALL_CATEGORIES
    : ALL_CATEGORIES.filter(c => c !== 'workshop');

  const parseAmount = (v: string) => parseFloat(v.replace(/\./g, '').replace(',', '.')) || 0;

  const [foreignCurrency, setForeignCurrency] = useState<string | null>(null);

  const handleScan = async (file: File, useSonnet = false) => {
    setScanState('scanning');
    setScanError(null);
    setForeignCurrency(null);
    try {
      const result = await scanReceipt(file, useSonnet);
      const f = result.fields;
      setQuota(result.quota);

      // Hvis bilaget er i fremmed valuta → vis advarsel i stedet for at udfylde
      if (f.currency && f.currency.toUpperCase() !== settings.currency) {
        setForeignCurrency(f.currency.toUpperCase());
        setScanState('failed');
        setScanError('foreign_currency');
        return;
      }

      if (f.date)        setDate(f.date);
      if (f.amount)      setAmount(String(f.amount));
      if (f.vat_amount)  setVatAmount(String(f.vat_amount));
      if (f.description) setDescription(f.description);
      if (f.category && ['insurance','tax','cleaning','parking','workshop','other'].includes(f.category))
        setCategory(f.category as ExpenseCategory);
      setScanState('success');
    } catch (err: any) {
      setQuota(err.quota ?? quota);
      if (err.message === 'upgrade_required') {
        setScanError('upgrade_required');
      } else if (err.message === 'quota_exceeded') {
        setScanError('quota_exceeded');
      } else {
        setScanError('failed');
      }
      setScanState('failed');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, useSonnet = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    handleScan(file, useSonnet);
  };

  const handleSave = async () => {
    setSaving(true);
    const data = {
      vehicle_id: vehicleId,
      date,
      category,
      description: description || undefined,
      amount: parseAmount(amount),
      vat_status: vatStatus,
      vat_amount: vatAmount ? parseAmount(vatAmount) : null,
      notes: notes || undefined,
    };
    if (expense?.id) {
      await updateExpense(expense.id, data);
    } else if (isRecurring) {
      await createRecurringExpense({ ...data, frequency, next_due_date: date });
    } else {
      await createExpense(data);
    }
    setSaving(false);
    onSaved();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-panel max-w-lg">
        <div className="flex items-center justify-between p-4 sm:p-6 border-b sticky top-0 bg-[var(--color-surface)] z-10">
          <h2 className="modal-title">
            {expense?.id
              ? (editing ? t('expense.title_edit') : t('expense.title'))
              : (expense ? t('expense.title_copy') : t('expense.title'))}
          </h2>
          <button onClick={onClose} className="modal-close">&times;</button>
        </div>

        <div className="p-4 sm:p-6 space-y-4">
          {/* Scope badge — only shown when viewing/editing existing expense */}
          {expense?.id && (
            <p className="text-sm text-gray-500">
              <span className="inline-block px-2 py-0.5 bg-[var(--color-primary-soft-bg)] text-[var(--color-primary-hover)] rounded text-xs font-medium">
                {vehicleId ? (vehicleLabel || t('expense.scope_vehicle')) : t('expense.scope_fleet')}
              </span>
            </p>
          )}

          {!editing ? (
            /* ── View mode ── */
            <dl className="space-y-3 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="label">{t('events.date')}</dt>
                  <dd className="text-gray-800">{date}</dd>
                </div>
                <div>
                  <dt className="label">{t('expense.category_label')}</dt>
                  <dd className="text-gray-800">{t(`expense.category_${category}`)}</dd>
                </div>
              </div>
              {description && (
                <div>
                  <dt className="label">{t('expense.description')}</dt>
                  <dd className="text-gray-800">{description}</dd>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <dt className="label">{t('events.cost')}</dt>
                  <dd className="text-gray-800 font-mono">{amount} {settings.currency}</dd>
                </div>
                <div>
                  <dt className="label">{t('fleet.col_vat')}</dt>
                  <dd><span className={`badge badge-vat-${vatStatus}`}>{t(`vat.${vatStatus}`)}</span>
                    {vatAmount && <span className="ml-2 text-gray-500 font-mono text-xs">{vatAmount} {settings.currency}</span>}
                  </dd>
                </div>
              </div>
              {notes && (
                <div>
                  <dt className="label">{t('expense.notes')}</dt>
                  <dd className="text-gray-700 whitespace-pre-wrap">{notes}</dd>
                </div>
              )}
              {expense?.id && (
                <div>
                  <dt className="label">Bilag</dt>
                  <dd className="mt-2"><ExpenseAttachmentsSection expenseId={expense.id} viewOnly /></dd>
                </div>
              )}
            </dl>
          ) : (
            /* ── Edit mode ── */
            <>
              {/* ── Scan banner — only for new expenses ── */}
              {!expense?.id && (
                <div className={`rounded-lg border px-4 py-3 text-sm ${
                  scanState === 'success' ? 'bg-[var(--color-success-100)] border-green-200 text-green-800' :
                  scanState === 'failed'  ? 'bg-[var(--color-error-100)] border-red-200 text-red-800' :
                  'bg-gray-50 border-gray-200 text-gray-700'
                }`}>
                  {/* Hidden file inputs */}
                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                    className="hidden" onChange={e => handleFileChange(e, false)} />

                  {scanState === 'idle' && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-500 text-xs">{t('expense.scan_button')} — JPG, PNG, WebP, PDF</span>
                      <button type="button" onClick={() => fileInputRef.current?.click()}
                        className="btn btn-secondary text-xs py-1 px-3">
                        {t('expense.scan_button')}
                      </button>
                    </div>
                  )}

                  {(scanState === 'scanning') && (
                    <div className="flex items-center gap-2">
                      <svg className="animate-spin h-4 w-4 text-[var(--color-primary)]" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                      </svg>
                      <span>{t('expense.scan_scanning')}</span>
                    </div>
                  )}

                  {scanState === 'success' && (
                    <div className="flex items-center justify-between">
                      <span>{t('expense.scan_success')}</span>
                      <button type="button" onClick={() => { setScanState('idle'); setScanError(null); }}
                        className="text-xs underline opacity-60 hover:opacity-100">
                        {t('expense.scan_button')}
                      </button>
                    </div>
                  )}

                  {scanState === 'failed' && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span>{scanError === 'foreign_currency'
                               ? t('expense.scan_foreign_currency', { currency: foreignCurrency, defaultCurrency: settings.currency })
                               : scanError === 'quota_exceeded' ? t('expense.scan_quota_exceeded')
                               : scanError === 'upgrade_required' ? ''
                               : t('expense.scan_failed')}</span>
                        <button type="button" onClick={() => { setScanState('idle'); setScanError(null); setForeignCurrency(null); }}
                          className="text-xs underline opacity-60 hover:opacity-100">
                          {scanError === 'foreign_currency' ? t('expense.scan_manual') : t('expense.scan_manual')}
                        </button>
                      </div>
                      {scanError === 'failed' && quota && quota.plan !== 'free' && quota.plan !== 'trial' && (
                        <div className="flex items-center gap-3">
                          <input type="file" accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                            className="hidden" id="scan-pro-input"
                            onChange={e => handleFileChange(e, true)} />
                          <label htmlFor="scan-pro-input"
                            className="cursor-pointer btn btn-secondary text-xs py-1 px-3">
                            {t('expense.scan_retry_pro')}
                          </label>
                          {quota && quota.sonnet_limit > 0 && (
                            <span className="text-xs opacity-70">
                              {t('expense.scan_quota', { used: quota.sonnet_used, limit: quota.sonnet_limit })}
                            </span>
                          )}
                        </div>
                      )}
                      {(scanError === 'upgrade_required' || (scanError === 'failed' && (quota?.plan === 'free' || quota?.plan === 'trial'))) && (
                        <p className="text-xs opacity-70">{t('expense.scan_upgrade')}</p>
                      )}
                    </div>
                  )}

                  {/* Pro Scan saldo — shown after a successful scan if plan has sonnet */}
                  {scanState === 'success' && quota && quota.sonnet_limit > 0 && (
                    <p className="mt-1 text-xs opacity-60">
                      {t('expense.scan_quota', { used: quota.sonnet_used, limit: quota.sonnet_limit })}
                    </p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="label">{t('events.date')}</label>
                  <input type="date" className="input" value={date} onChange={e => setDate(e.target.value)} />
                </div>
                <div>
                  <label className="label">{t('expense.category_label')}</label>
                  <select className="input" value={category} onChange={e => setCategory(e.target.value as ExpenseCategory)}>
                    {selectableCategories.map(c => (
                      <option key={c} value={c}>{t(`expense.category_${c}`)}</option>
                    ))}
                  </select>
                  {category === 'workshop' && (
                    <p className="text-xs text-ink-500 mt-1">{t('expense.workshop_is_event')}</p>
                  )}
                </div>
              </div>
              {/* Vehicle selector — only for new expenses (create or copy) */}
              {!expense?.id && (
                <div>
                  <label className="label">{t('fleet.col_vehicle')}</label>
                  <div className="relative">
                    <input
                      className="input"
                      placeholder={t('expense.vehicle_placeholder')}
                      value={vehicleSearch || vehicleLabel}
                      onChange={e => { setVehicleSearch(e.target.value); setVehicleLabel(''); setVehicleId(null); }}
                    />
                    {vehicleId && (
                      <button
                        type="button"
                        onClick={() => { setVehicleId(null); setVehicleLabel(''); setVehicleSearch(''); }}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs">
                        {t('expense.scope_fleet')} ✕
                      </button>
                    )}
                    {vehicleResults.length > 0 && (
                      <ul className="absolute left-0 top-full z-10 bg-white border rounded shadow-lg w-full text-sm">
                        {vehicleResults.map(v => (
                          <li
                            key={v.id}
                            className="px-3 py-2 hover:bg-[var(--color-primary-soft-bg)] cursor-pointer font-mono"
                            onClick={() => { setVehicleId(v.id); setVehicleLabel(v.registration_number); setVehicleSearch(''); setVehicleResults([]); }}>
                            {v.registration_number}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  {vehicleId && (
                    <p className="mt-1 text-xs text-[var(--color-success)]">{vehicleLabel}</p>
                  )}
                </div>
              )}
              <div>
                <label className="label">{t('expense.description')}</label>
                <input className="input" value={description} onChange={e => setDescription(e.target.value)} />
              </div>
              <PriceInput
                label={t('events.cost')}
                amount={amount}
                vatStatus={vatStatus}
                vatAmount={vatAmount}
                onAmountChange={setAmount}
                onVatStatusChange={setVatStatus}
                onVatAmountChange={setVatAmount}
                vatRate={settings.vatRate}
              />
              <div>
                <label className="label">{t('expense.notes')}</label>
                <textarea className="input" rows={2} value={notes} onChange={e => setNotes(e.target.value)} />
              </div>

              {/* Bilag — kun for eksisterende expenses (kraever expense.id til Bunny-path) */}
              {expense?.id && (
                <div className="border-t pt-3">
                  <label className="label">Bilag</label>
                  <ExpenseAttachmentsSection expenseId={expense.id} />
                </div>
              )}

              {/* Fast udgift — kun ved oprettelse */}
              {!expense?.id && (
                <div className="border-t pt-3 space-y-2">
                  <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-700">
                    <input type="checkbox" className="rounded" checked={isRecurring}
                      onChange={e => setIsRecurring(e.target.checked)} />
                    {t('expense.save_as_recurring')}
                  </label>
                  {isRecurring && (
                    <div className="flex gap-3 pl-6">
                      {(['monthly', 'yearly'] as const).map(f => (
                        <label key={f} className="flex items-center gap-1.5 cursor-pointer text-sm">
                          <input type="radio" name="frequency" value={f} checked={frequency === f}
                            onChange={() => setFrequency(f)} />
                          {t(`expense.frequency_${f}`)}
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 pt-2">
          <button onClick={onClose} className="btn btn-secondary">{t('modal.close')}</button>
          {!editing ? (
            <button onClick={() => setEditing(true)} className="btn btn-primary">{t('events.edit_event')}</button>
          ) : (
            <button onClick={handleSave} disabled={saving || !amount} className="btn btn-primary">
              {saving ? '...' : t('modal.save')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
